// Admin auth context. Mirrors src/frontend/app/src/lib/auth.ts.
//
// State is hydrated by a single GET /api/admin/me probe on app load
// (RequireAdminAuth). 401 → anonymous → redirect to /login.

import { createContext, useContext } from 'react';
import type { AdminMeResponse } from '@shared/api-types';

export type AdminAuthState =
  | { status: 'unknown' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; admin: AdminMeResponse };

export interface AdminAuthContextValue {
  state: AdminAuthState;
  /** Re-probe /api/admin/me. Called after sign-in and on demand. */
  refresh: () => Promise<void>;
  /** Local sign-out — clears state and pings POST /api/admin/auth/logout. */
  signOut: () => Promise<void>;
}

export const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAdminAuth() must be used inside an <AdminAuthProvider>.');
  }
  return ctx;
}
